import React, { useEffect, useMemo, useRef, useState } from 'react'
import styled, { keyframes } from 'styled-components'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import MapRoundedIcon from '@mui/icons-material/MapRounded'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import { cardStyle, ink, line, font, surface } from 'features/dashboard/tokens'
import { scoreColour, formatPercent } from 'features/dashboard/charts/scoreColour'

const CACHE_KEY = 'bp.postcodeGeo.v1'
const VIEW_KEY = 'bp.portfoliosMapView.v1'
const normPc = pc => String(pc || '').toUpperCase().replace(/\s+/g, '')

const loadView = () => {
  try {
    const v = JSON.parse(localStorage.getItem(VIEW_KEY) || 'null')
    if (v && Number.isFinite(v.lat) && Number.isFinite(v.lng) && Number.isFinite(v.z)) return v
  } catch { /* ignore */ }
  return null
}
const saveView = v => {
  try { localStorage.setItem(VIEW_KEY, JSON.stringify(v)) } catch { /* ignore */ }
}

const loadCache = () => {
  try { return JSON.parse(localStorage.getItem(CACHE_KEY) || '{}') } catch { return {} }
}
const saveCache = c => {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify(c)) } catch {}
}

const geocodeBulk = async postcodes => {
  const batches = []
  for (let i = 0; i < postcodes.length; i += 100) batches.push(postcodes.slice(i, i + 100))
  const lists = await Promise.all(batches.map(async batch => {
    try {
      const res = await fetch('https://api.postcodes.io/postcodes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ postcodes: batch }),
      })
      const json = await res.json()
      return json.result || []
    } catch (e) {
      return []
    }
  }))
  const found = {}
  lists.flat().forEach(row => {
    if (row?.result?.latitude != null) {
      found[normPc(row.query)] = { lat: row.result.latitude, lng: row.result.longitude }
    }
  })
  return found
}

const PassportsMap = ({ points = [], onOpenBp, showCompliance = true, loading = false, defaultOpen = true, fitToPoints = false }) => {
  const [open, setOpen] = useState(defaultOpen)
  const [geo, setGeo] = useState(loadCache)
  const [geocoding, setGeocoding] = useState(false)
  const containerRef = useRef(null)
  const mapRef = useRef(null)
  const layerRef = useRef(null)
  const programmaticRef = useRef(false)
  const prevSigRef = useRef(null)
  const restoredRef = useRef(false)
  const onOpenRef = useRef(onOpenBp)
  onOpenRef.current = onOpenBp

  const hasPoints = points.length > 0

  useEffect(() => {
    const missing = [...new Set(
      points
        .filter(p => !(Number.isFinite(p.lat) && Number.isFinite(p.lng)))
        .map(p => normPc(p.postcode))
        .filter(pc => pc && !geo[pc])
    )]
    if (missing.length === 0) return
    let active = true
    setGeocoding(true)
    geocodeBulk(missing).then(found => {
      if (!active) return
      if (Object.keys(found).length) {
        setGeo(prev => {
          const next = { ...prev, ...found }
          saveCache(next)
          return next
        })
      }
      setGeocoding(false)
    })
    return () => { active = false }
    // eslint-disable-next-line
  }, [points])

  useEffect(() => {
    if (!open || mapRef.current || !containerRef.current) return
    const el = containerRef.current
    const map = L.map(el, {
      dragging: true,
      touchZoom: true,
      scrollWheelZoom: false,
      doubleClickZoom: true,
      boxZoom: true,
      keyboard: true,
      zoomControl: true,
      preferCanvas: false,
      attributionControl: false,
    })
    const saved = fitToPoints ? null : loadView()
    if (saved) map.setView([saved.lat, saved.lng], saved.z)
    else map.setView([54.5, -3], 5)
    restoredRef.current = !!saved
    prevSigRef.current = null
    L.tileLayer('https://services.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}', {
      attribution: '&copy; Esri, HERE, Garmin &amp; OpenStreetMap contributors',
      maxZoom: 20,
    }).addTo(map)
    L.tileLayer('https://services.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Reference/MapServer/tile/{z}/{y}/{x}', {
      maxZoom: 20,
    }).addTo(map)
    mapRef.current = map

    map.on('moveend', () => {
      if (programmaticRef.current) { programmaticRef.current = false; return }
      if (fitToPoints) return
      const c = map.getCenter()
      saveView({ lat: c.lat, lng: c.lng, z: map.getZoom() })
    })

    map.invalidateSize()
    const ro = new ResizeObserver(() => map.invalidateSize())
    ro.observe(el)
    return () => {
      ro.disconnect()
      map.remove()
      mapRef.current = null
      layerRef.current = null
    }
  }, [open, hasPoints, fitToPoints])

  const located = useMemo(
    () => points.map(p => ({
      ...p,
      g: (Number.isFinite(p.lat) && Number.isFinite(p.lng)) ? { lat: p.lat, lng: p.lng } : geo[normPc(p.postcode)],
    })).filter(p => p.g),
    [points, geo]
  )

  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    if (layerRef.current) {
      layerRef.current.remove()
      layerRef.current = null
    }
    if (located.length === 0) return
    const group = L.layerGroup()
    const bounds = []
    located.forEach(p => {
      const marker = L.circleMarker([p.g.lat, p.g.lng], {
        radius: 8,
        weight: 2,
        color: '#ffffff',
        fillColor: showCompliance ? scoreColour(p.score) : '#D24724',
        fillOpacity: 1,
      })
      const label = (showCompliance && p.score !== null && p.score !== undefined)
        ? `${p.name} — ${formatPercent(p.score)}`
        : p.name
      marker.bindTooltip(label, { direction: 'top' })
      marker.on('click', () => onOpenRef.current && onOpenRef.current(p.id))
      marker.addTo(group)
      bounds.push([p.g.lat, p.g.lng])
    })
    group.addTo(map)
    layerRef.current = group

    const sig = located.map(p => p.id).sort().join(',')
    const setChanged = sig !== prevSigRef.current
    prevSigRef.current = sig

    if (setChanged) {
      if (restoredRef.current) {
        restoredRef.current = false
      } else {
        programmaticRef.current = true
        map.fitBounds(bounds, { padding: [36, 36], maxZoom: fitToPoints ? 15 : 13 })
      }
    }
  }, [located, open, showCompliance, fitToPoints])

  if (loading && !hasPoints) {
    return (
      <Panel>
        <Head as='div' aria-busy='true' style={{ cursor: 'default' }}>
          <MapRoundedIcon style={{ fontSize: 16 }} />
          <HeadText>Map</HeadText>
          <HeadMeta>loading…</HeadMeta>
        </Head>
        <MapWrap>
          <MapSkeleton aria-hidden='true' />
        </MapWrap>
      </Panel>
    )
  }

  if (!hasPoints) return null

  const overlay = located.length === 0
    ? (geocoding ? `Locating ${points.length} Building Passport${points.length === 1 ? '' : 's'}…` : 'No mappable locations.')
    : null

  return (
    <Panel>
      <Head type='button' onClick={() => setOpen(o => !o)} aria-expanded={open}>
        {open
          ? <ExpandMoreRoundedIcon style={{ fontSize: 18 }} />
          : <ChevronRightRoundedIcon style={{ fontSize: 18 }} />}
        <MapRoundedIcon style={{ fontSize: 16 }} />
        <HeadText>Map</HeadText>
        <HeadMeta>
          {located.length} of {points.length} located{geocoding ? ' · locating…' : ''}
        </HeadMeta>
      </Head>
      {open && (
        <MapWrap>
          <MapBox ref={containerRef} />
          {overlay && <Overlay>{overlay}</Overlay>}
          {showCompliance && located.length > 0 && (
            <Legend aria-hidden='true'>
              <LegendItem><Dot $c='#b00020' />At risk</LegendItem>
              <LegendItem><Dot $c='#d97706' />Needs attention</LegendItem>
              <LegendItem><Dot $c='#15803d' />Healthy</LegendItem>
              <LegendItem><Dot $c='#cbd5e1' />Setting up</LegendItem>
            </Legend>
          )}
        </MapWrap>
      )}
    </Panel>
  )
}

export default PassportsMap

const Panel = styled.div`
  ${cardStyle}
  overflow: hidden;
  margin-bottom: 20px;
`

const Head = styled.button`
  font: inherit;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border: none;
  background: ${surface.card};
  color: ${ink.base};
  cursor: pointer;
  text-align: left;

  &:hover { background: ${surface.wash}; }
`

const HeadText = styled.span`
  font-size: ${font.emphasis};
  font-weight: 700;
`

const HeadMeta = styled.span`
  margin-left: auto;
  margin-right: 10px;
  font-size: ${font.small};
  color: ${ink.mute};
  font-variant-numeric: tabular-nums;
`

const MapWrap = styled.div`
  position: relative;
  isolation: isolate;
  z-index: 0;
  border-top: 1px solid ${line.base};
`

const fade = keyframes`
  0%, 100% { opacity: 0.6; }
  50%      { opacity: 1; }
`

const MapSkeleton = styled.div`
  width: 100%;
  height: 240px;
  background: ${surface.wash};
  animation: ${fade} 1.4s ease-in-out infinite;
`

const MapBox = styled.div`
  position: relative;
  z-index: 0;
  width: 100%;
  height: 240px;

  .leaflet-container {
    width: 100%;
    height: 100%;
    background: ${surface.wash};
    font: inherit;
  }
`

const Legend = styled.div`
  position: absolute;
  bottom: 10px;
  left: 10px;
  z-index: 401;
  pointer-events: none;
  display: flex;
  gap: 12px;
  padding: 6px 10px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid ${line.base};
  font-size: ${font.caption};
  color: ${ink.mute};
  font-weight: 600;
`

const LegendItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
`

const Dot = styled.span`
  width: 8px; height: 8px; border-radius: 50%;
  background: ${({ $c }) => $c};
`

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 400;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  font-size: ${font.body};
  font-weight: 600;
  color: ${ink.mute};
  background: rgba(255, 255, 255, 0.82);
  animation: ${fade} 1.4s ease-in-out infinite;
`
